import React from "react";
import { toast } from "sonner";
import { UserCheck, UserX, RefreshCw, FileText, Download, ChevronDown, ChevronUp, Building2, AlertCircle, CheckCircle2 } from "lucide-react";
import { adminApi, API } from "../lib/api";

const DOC_LABELS = {
  criminal_record: "Straffeattest",
  drivers_license: "Kørekort",
  employment_contract: "Ansættelseskontrakt",
  residence_permit: "Opholdstilladelse",
  work_permit: "Arbejdstilladelse",
};

export default function PendingDriversPanel() {
  const [list, setList] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);
  const [busy, setBusy] = React.useState(null); // driver_id of action in progress

  const load = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/drivers/pending");
      setList(data || []);
    } catch (e) {
      toast.error("Kunne ikke hente afventende chauffører");
      setList([]);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const downloadDoc = async (driverId, docId, filename) => {
    const token = localStorage.getItem("ema_admin_token");
    try {
      const res = await fetch(`${API}/admin/drivers/${driverId}/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Kunne ikke downloade dokument"); }
  };

  const approve = async (driver) => {
    const notes = prompt(`Godkend ${driver.name}? Indtast valgfri note:`);
    if (notes === null) return; // cancelled
    setBusy(driver.id);
    try {
      await adminApi().post(`/admin/drivers/${driver.id}/approve`, { notes });
      toast.success(`${driver.name} godkendt — vognmanden får besked på email`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke godkende");
    } finally { setBusy(null); }
  };

  const reject = async (driver) => {
    const reason = prompt(`Afvis ${driver.name}? Indtast årsag (sendes til vognmanden):`);
    if (!reason || reason.length < 3) return;
    setBusy(driver.id);
    try {
      await adminApi().post(`/admin/drivers/${driver.id}/reject`, { reason });
      toast.success(`${driver.name} afvist — vognmanden får besked`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke afvise");
    } finally { setBusy(null); }
  };

  if (list === null) return <div className="text-brand-text2 p-6" data-testid="pending-drivers-loading">Indlæser...</div>;

  return (
    <div className="space-y-4" data-testid="pending-drivers-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="kicker">Godkendelser</div>
          <h2 className="font-display font-bold text-2xl text-brand-dark mt-1">Afventende chauffører ({list.length})</h2>
          <p className="text-sm text-brand-text2 mt-1">Gennemgå dokumenter, godkend eller afvis chauffører oprettet af vognmænd.</p>
        </div>
        <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="pending-drivers-refresh">
          <RefreshCw size={14} /> Opdatér
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="pending-drivers-empty">
          <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen afventende</div>
          <p className="text-sm text-brand-text2 mt-1">Alle ansøgninger er gennemgået.</p>
        </div>
      ) : (
        list.map((d) => {
          const isOpen = expanded === d.id;
          return (
            <div key={d.id} className="bg-white border border-brand" data-testid={`pending-driver-${d.id}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : d.id)}
                className="w-full p-4 text-left flex items-center justify-between gap-3 hover:bg-brand-bg2"
                data-testid={`pending-driver-toggle-${d.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-bold text-brand-dark">{d.name}</div>
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 font-bold uppercase tracking-widest">Afventer</span>
                  </div>
                  <div className="text-xs text-brand-text2 mt-1 truncate">{d.email} {d.phone ? `· ${d.phone}` : ""}</div>
                  {d.subcontractor && (
                    <div className="text-xs text-brand-text2 mt-1 inline-flex items-center gap-1">
                      <Building2 size={11} /> {d.subcontractor.company_name} (CVR {d.subcontractor.cvr})
                    </div>
                  )}
                  <div className="text-xs text-brand-text2 mt-1">{d.documents?.length || 0} dokumenter · oprettet {new Date(d.created_at).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short" })}</div>
                </div>
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              {isOpen && (
                <div className="border-t border-brand p-4 bg-brand-bg2 space-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Uploadede dokumenter</div>
                    <div className="space-y-2">
                      {(d.documents || []).map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between gap-3 bg-white border border-brand p-3" data-testid={`pending-driver-doc-${doc.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={16} className="text-brand-red flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="font-semibold text-brand-dark text-sm">{DOC_LABELS[doc.doc_type] || doc.doc_type}</div>
                              <div className="text-xs text-brand-text2 truncate">{doc.filename} · {(doc.size_bytes / 1024).toFixed(0)} KB</div>
                            </div>
                          </div>
                          <button
                            onClick={() => downloadDoc(d.id, doc.id, doc.filename)}
                            className="btn-secondary inline-flex items-center gap-1 text-xs flex-shrink-0"
                            data-testid={`pending-driver-doc-dl-${doc.id}`}
                          >
                            <Download size={12} /> Hent
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-brand">
                    <button
                      onClick={() => approve(d)}
                      disabled={busy === d.id}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`pending-driver-approve-${d.id}`}
                    >
                      {busy === d.id ? <RefreshCw size={14} className="animate-spin" /> : <UserCheck size={14} />}
                      Godkend
                    </button>
                    <button
                      onClick={() => reject(d)}
                      disabled={busy === d.id}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-bold uppercase tracking-wider"
                      data-testid={`pending-driver-reject-${d.id}`}
                    >
                      <UserX size={14} /> Afvis
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
